/**
 * CatalogModelDialog: add a catalog model on a public upstream, or edit
 * an existing one (display name, provider model, price, reasoning
 * effort, whether it reads images). Slug and upstream are fixed after
 * creation: the slug is the string customers have in their code, and the
 * gateway slot lives on one upstream.
 *
 * Saving needs a passing sample call for the upstream, provider model and
 * params about to be written. The call goes through the gateway with the
 * upstream's own key, so a typo in the provider model fails here instead
 * of on a customer's first request. With Reads images checked the call
 * also sends a small red image and passes only when the reply names the
 * color, so a text-only model cannot be marked as reading images (the
 * workbench would send it images and every such call would fail).
 * Unchecking the box needs no call (needsSampleCall in ./catalog).
 *
 * The caller owns the open state; the dialog owns the form and the
 * mutations, and closes itself on success.
 */

import { useState, type FormEvent } from 'react'
import { CheckCircle2, Loader2, XCircle } from 'lucide-react'
import { toast } from 'sonner'

import { getApiErrorDetail } from '@/api/client'
import type {
  CatalogModelView,
  CatalogProbeResponse,
  CatalogUpstreamView,
} from '@/api/generated/model'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useCreateCatalogModel,
  useProbeCatalogModel,
  useUpdateCatalogModel,
} from '@/hooks/useCatalog'

import {
  CATALOG_SLUG_REGEX,
  REASONING_EFFORTS,
  initialCatalogForm,
  needsSampleCall,
  parseCatalogForm,
  probeKey,
  probeLine,
  probeRequest,
  type CatalogFormState,
} from './catalog'

/** The Select cannot carry '' as a value, so the provider default gets a sentinel. */
const PROVIDER_DEFAULT = 'default'

interface CatalogModelDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  upstreams: CatalogUpstreamView[]
  /** The model to edit, or null to add one. */
  model: CatalogModelView | null
}

interface ProbeOutcome {
  key: string
  /** Whether the call sent the test image, fixed when it went out. */
  image: boolean
  result: CatalogProbeResponse
}

export function CatalogModelDialog({
  open,
  onOpenChange,
  upstreams,
  model,
}: CatalogModelDialogProps): React.JSX.Element {
  const [form, setForm] = useState<CatalogFormState>(() => initialCatalogForm(model))
  const [error, setError] = useState<string | null>(null)
  const [probe, setProbe] = useState<ProbeOutcome | null>(null)
  const createMutation = useCreateCatalogModel()
  const updateMutation = useUpdateCatalogModel()
  const probeMutation = useProbeCatalogModel()
  const isPending = createMutation.isPending || updateMutation.isPending
  const isEdit = model !== null
  const existingParams = model?.params ?? null
  // A slot can carry an effort the list does not offer; keep it selectable.
  const efforts: readonly string[] = REASONING_EFFORTS.includes(
    form.reasoning_effort as (typeof REASONING_EFFORTS)[number],
  )
    ? REASONING_EFFORTS
    : form.reasoning_effort === ''
      ? REASONING_EFFORTS
      : [...REASONING_EFFORTS, form.reasoning_effort]

  const set = <K extends keyof CatalogFormState>(field: K, value: CatalogFormState[K]): void => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  const slugValid = isEdit || CATALOG_SLUG_REGEX.test(form.slug)
  const request = probeRequest(form, model)
  const currentKey = probeKey(request)
  const canProbe =
    !probeMutation.isPending && !isPending && form.upstream_id !== '' && request.provider_model !== ''
  const tested =
    !needsSampleCall(request, model) || (probe?.key === currentKey && probe.result.ok)
  const canSubmit =
    !isPending && slugValid && form.display_name.trim() !== '' && canProbe && tested

  const runProbe = (): void => {
    const image = request.image === true
    probeMutation.mutate(request, {
      onSuccess: result => {
        setProbe({ key: currentKey, image, result })
      },
      onError: err => {
        setProbe({
          key: currentKey,
          image,
          result: { ok: false, status: 0, detail: getApiErrorDetail(err) ?? 'The test call failed.' },
        })
      },
    })
  }

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault()
    if (!canSubmit) return
    const parsed = parseCatalogForm(form, existingParams)
    if (typeof parsed === 'string') {
      setError(parsed)
      return
    }
    setError(null)
    const onError = (err: unknown): void => {
      setError(getApiErrorDetail(err) ?? 'The catalog change failed.')
    }
    const onSuccess = (saved: CatalogModelView): void => {
      toast.success(`${saved.slug} saved. The gateway serves it now.`)
      onOpenChange(false)
    }
    const common = {
      display_name: form.display_name.trim(),
      provider_model: request.provider_model,
      pricing: parsed.pricing,
      params: parsed.params,
      input_modalities: parsed.input_modalities,
    }
    if (model === null) {
      createMutation.mutate(
        { ...common, slug: form.slug, upstream_id: form.upstream_id },
        { onSuccess, onError },
      )
    } else {
      updateMutation.mutate({ id: model.id, body: common }, { onSuccess, onError })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit ${model.slug}` : 'Add catalog model'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Changes reach the gateway as soon as you save.'
              : 'The model is callable by every team as soon as you save.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="catalog-upstream">Upstream</Label>
            <Select
              value={form.upstream_id}
              disabled={isEdit || isPending}
              onValueChange={value => {
                set('upstream_id', value)
              }}
            >
              <SelectTrigger id="catalog-upstream">
                <SelectValue placeholder="Pick a provider" />
              </SelectTrigger>
              <SelectContent>
                {upstreams.map(upstream => (
                  <SelectItem key={upstream.id} value={upstream.id}>
                    {upstream.name} <span className="font-mono text-xs">({upstream.id})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="catalog-slug">Slug</Label>
            <Input
              id="catalog-slug"
              className="font-mono"
              value={form.slug}
              placeholder="openai/gpt-7"
              disabled={isEdit || isPending}
              aria-invalid={!slugValid}
              aria-describedby="catalog-slug-hint"
              onChange={e => {
                set('slug', e.target.value)
              }}
            />
            <p id="catalog-slug-hint" className="text-xs text-muted-foreground">
              What customers send as the model name. One of the catalog
              namespaces (anthropic, openai, google, fireworks, baseten, wandb),
              a slash, then lowercase letters, digits, dots and hyphens.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="catalog-display-name">Display name</Label>
            <Input
              id="catalog-display-name"
              value={form.display_name}
              placeholder="GPT-7"
              disabled={isPending}
              onChange={e => {
                set('display_name', e.target.value)
              }}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="catalog-provider-model">Provider model</Label>
            <Input
              id="catalog-provider-model"
              className="font-mono"
              value={form.provider_model}
              placeholder="gpt-7"
              disabled={isPending}
              aria-describedby="catalog-provider-model-hint"
              onChange={e => {
                set('provider_model', e.target.value)
              }}
            />
            <p id="catalog-provider-model-hint" className="text-xs text-muted-foreground">
              The model string the gateway forwards to the provider.
            </p>
          </div>

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">List price, $ per 1M tokens</legend>
            <div className="grid grid-cols-3 gap-2">
              {(
                [
                  ['input_per_m', 'Input'],
                  ['cached_per_m', 'Cached input'],
                  ['output_per_m', 'Output'],
                ] as const
              ).map(([field, label]) => (
                <div key={field} className="space-y-1">
                  <Label htmlFor={`catalog-${field}`} className="text-xs">
                    {label}
                  </Label>
                  <Input
                    id={`catalog-${field}`}
                    inputMode="decimal"
                    value={form[field]}
                    placeholder="0"
                    disabled={isPending}
                    onChange={e => {
                      set(field, e.target.value)
                    }}
                  />
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              The provider's standard tier with no markup. A change re-rates
              every billed team's current period for this slug.
            </p>
          </fieldset>

          <div className="space-y-2">
            <Label htmlFor="catalog-reasoning-effort">Reasoning effort</Label>
            <Select
              value={form.reasoning_effort === '' ? PROVIDER_DEFAULT : form.reasoning_effort}
              disabled={isPending}
              onValueChange={value => {
                set('reasoning_effort', value === PROVIDER_DEFAULT ? '' : value)
              }}
            >
              <SelectTrigger id="catalog-reasoning-effort" aria-describedby="catalog-reasoning-hint">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={PROVIDER_DEFAULT}>Provider default</SelectItem>
                {efforts.map(effort => (
                  <SelectItem key={effort} value={effort}>
                    {effort}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p id="catalog-reasoning-hint" className="text-xs text-muted-foreground">
              Sent on every call to this slug. Pick none for a model that
              should answer without thinking.
            </p>
          </div>

          <div className="flex items-start gap-2">
            <Checkbox
              id="catalog-reads-images"
              className="mt-0.5"
              checked={form.reads_images}
              disabled={isPending}
              aria-describedby="catalog-reads-images-hint"
              onCheckedChange={checked => {
                set('reads_images', checked === true)
              }}
            />
            <div className="space-y-1">
              <Label htmlFor="catalog-reads-images">Reads images</Label>
              <p id="catalog-reads-images-hint" className="text-xs text-muted-foreground">
                The workbench offers its image tool only to models with this checked.
                When it is checked, the sample call sends a small red image and
                passes only if the reply names the color.
              </p>
            </div>
          </div>

          <div className="space-y-2 rounded-md border border-border p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium">Sample call</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!canProbe}
                onClick={runProbe}
              >
                {probeMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Test model
              </Button>
            </div>
            <ProbeStatus probe={probe} currentKey={currentKey} tested={tested} />
          </div>

          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={isPending}
              onClick={() => {
                onOpenChange(false)
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {isEdit ? 'Save' : 'Add model'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

interface ProbeStatusProps {
  probe: ProbeOutcome | null
  currentKey: string
  tested: boolean
}

function ProbeStatus({ probe, currentKey, tested }: ProbeStatusProps): React.JSX.Element {
  if (probe === null || probe.key !== currentKey) {
    return (
      <p className="text-xs text-muted-foreground" aria-live="polite">
        {tested
          ? 'This upstream, provider model and reasoning effort already serve traffic.'
          : 'Send one short request to the provider before you save.'}
      </p>
    )
  }
  const { result } = probe
  return (
    <p
      className={`flex items-start gap-1.5 text-xs ${result.ok ? 'text-foreground' : 'text-destructive'}`}
      aria-live="polite"
    >
      {result.ok ? (
        <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      ) : (
        <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      )}
      <span>{probeLine(probe.image, result)}</span>
    </p>
  )
}
